import { useEffect } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import type { ProjectConfig, ProjectConfigInput } from '@cms/models';
import { Button } from '@/components/ui/button';
import { Toggle } from '@/components/ui/toggle';
import { CommonModal } from '@/components/common/CommonModal';
import { cn } from '@/lib/utils';
import { extractApiErrorMessage } from '@/lib/api/extract-api-error-message';
import { useCreateProjectConfig, useUpdateProjectConfig } from '@/lib/api/project-configs';

type ProjectConfigFormData = {
  order_num: number;
  key: string;
  value: string;
  type: 'css_selector' | 'xpath' | 'regex' | 'workflow_document';
  status: 'active' | 'inactive';
  notes: string;
  document_json: string;
};

const inputClass =
  'mt-1 block w-full h-9 px-3 py-1.5 text-sm bg-background border border-border rounded-md focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors';

const textareaClass =
  'mt-1 block w-full px-3 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors resize-y';
const PROJECT_CONFIG_TYPE_OPTIONS = [
  { value: 'workflow_document', label: 'Workflow JSON' },
  { value: 'css_selector', label: 'CSS Selector (legacy)' },
  { value: 'xpath', label: 'XPath (legacy)' },
  { value: 'regex', label: 'Regex (legacy)' },
] as const;

export type ProjectConfigFormModalMode = 'create' | 'view' | 'edit';

interface ProjectConfigFormModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly mode: ProjectConfigFormModalMode;
  readonly projectConfig: ProjectConfig | null;
  readonly onSuccess?: () => void;
}

function getOrderNum(item: ProjectConfig): number {
  const maybeCamel = (item as ProjectConfig & { orderNum?: unknown }).orderNum;
  if (typeof item.order_num === 'number') return item.order_num;
  if (typeof maybeCamel === 'number') return maybeCamel;
  return 0;
}

export function ProjectConfigFormModal({
  open,
  onClose,
  mode,
  projectConfig,
  onSuccess,
}: ProjectConfigFormModalProps) {
  const isCreate = mode === 'create';
  const isView = mode === 'view';

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ProjectConfigFormData>({
    defaultValues: {
      order_num: 0,
      key: '',
      value: '',
      type: 'workflow_document',
      status: 'active',
      notes: '',
      document_json: '',
    },
  });

  useEffect(() => {
    if (projectConfig) {
      reset({
        order_num: getOrderNum(projectConfig),
        key: projectConfig.key ?? '',
        value: projectConfig.value ?? '',
        type: projectConfig.type ?? 'workflow_document',
        status: projectConfig.status === 'inactive' ? 'inactive' : 'active',
        notes: projectConfig.notes ?? '',
        document_json: projectConfig.document
          ? JSON.stringify(projectConfig.document, null, 2)
          : '',
      });
      return;
    }
    if (isCreate) {
      reset({
        order_num: 0,
        key: '',
        value: '',
        type: 'workflow_document',
        status: 'active',
        notes: '',
        document_json: '',
      });
    }
  }, [projectConfig, isCreate, reset]);

  const selectedType = useWatch({ control, name: 'type' });
  const createMutation = useCreateProjectConfig();
  const updateMutation = useUpdateProjectConfig();

  const onSubmit = async (data: ProjectConfigFormData) => {
    if (isView) return;
    let document: Record<string, unknown> | undefined;
    if (data.type === 'workflow_document') {
      const raw = data.document_json.trim();
      if (!raw) {
        alert('Workflow JSON document is required for workflow_document type.');
        return;
      }
      try {
        const parsed = JSON.parse(raw) as unknown;
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
          throw new Error('Document must be a JSON object');
        }
        document = parsed as Record<string, unknown>;
      } catch (error: unknown) {
        alert(error instanceof Error ? error.message : 'Invalid workflow JSON');
        return;
      }
    }
    const payload: ProjectConfigInput = {
      order_num: data.order_num,
      key: data.key.trim(),
      value: data.value,
      type: data.type,
      status: data.status,
      notes: data.notes.trim() || undefined,
      document,
    };
    try {
      if (isCreate) {
        await createMutation.mutateAsync(payload);
      } else if (projectConfig?.id) {
        await updateMutation.mutateAsync({ id: projectConfig.id, payload });
      }
      onSuccess?.();
      onClose();
    } catch (error: unknown) {
      alert(extractApiErrorMessage(error));
    }
  };

  const title =
    mode === 'create'
      ? 'Create Workflow Config'
      : mode === 'view'
        ? 'Workflow Config details'
        : 'Edit Workflow Config';

  return (
    <CommonModal
      open={open}
      onClose={onClose}
      title={title}
      titleId="project-config-form-modal-title"
      panelClassName="max-w-2xl max-h-[90vh] overflow-y-auto"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label htmlFor="project-config-order-num" className="block text-sm font-medium text-foreground">
              Order *
            </label>
            <input
              id="project-config-order-num"
              type="number"
              {...register('order_num', {
                required: 'Order is required',
                valueAsNumber: true,
              })}
              disabled={isView}
              className={cn(inputClass, isView && 'cursor-default bg-muted/50')}
            />
          </div>
          <div className="md:col-span-2">
            <label htmlFor="project-config-key" className="block text-sm font-medium text-foreground">
              Key *
            </label>
            <input
              id="project-config-key"
              type="text"
              {...register('key', { required: 'Key is required' })}
              disabled={isView}
              className={cn(inputClass, 'font-mono', isView && 'cursor-default bg-muted/50')}
            />
            {!isView && errors.key && (
              <p className="mt-1 text-sm text-destructive">{errors.key.message}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label htmlFor="project-config-type" className="block text-sm font-medium text-foreground">
              Type *
            </label>
            {isView ? (
              <input
                id="project-config-type"
                type="text"
                value={
                  PROJECT_CONFIG_TYPE_OPTIONS.find(option => option.value === projectConfig?.type)?.label ??
                  'CSS Selector'
                }
                disabled
                className={cn(inputClass, 'cursor-default bg-muted/50')}
              />
            ) : (
              <select
                id="project-config-type-select"
                {...register('type', { required: 'Type is required' })}
                className={inputClass}
              >
                {PROJECT_CONFIG_TYPE_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            )}
            {!isView && errors.type && (
              <p className="mt-1 text-sm text-destructive">{errors.type.message}</p>
            )}
          </div>
          <div className="flex items-end">
            {isView ? (
              <div>
                <span className="block text-sm font-medium text-muted-foreground mb-1">Status</span>
                <span className="inline-flex items-center rounded-md border border-border px-2.5 py-1 text-sm capitalize">
                  {projectConfig?.status ?? '—'}
                </span>
              </div>
            ) : (
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <Toggle
                    id="project-config-status"
                    label="Status"
                    checked={field.value === 'active'}
                    onCheckedChange={checked => field.onChange(checked ? 'active' : 'inactive')}
                  />
                )}
              />
            )}
          </div>
        </div>

        {(selectedType === 'workflow_document' || projectConfig?.type === 'workflow_document') && (
          <div>
            <label htmlFor="project-config-document" className="block text-sm font-medium text-foreground">
              Workflow JSON document *
            </label>
            <textarea
              id="project-config-document"
              rows={16}
              spellCheck={false}
              {...register('document_json', {
                required:
                  selectedType === 'workflow_document' ? 'Workflow JSON is required' : false,
              })}
              disabled={isView}
              className={cn(textareaClass, 'font-mono text-xs', isView && 'cursor-default bg-muted/50')}
            />
            {!isView && errors.document_json && (
              <p className="mt-1 text-sm text-destructive">{errors.document_json.message}</p>
            )}
          </div>
        )}

        <div>
          <label htmlFor="project-config-value" className="block text-sm font-medium text-foreground">
            Value (legacy)
          </label>
          <textarea
            id="project-config-value"
            rows={4}
            spellCheck={false}
            {...register('value')}
            disabled={isView}
            className={cn(textareaClass, 'font-mono', isView && 'cursor-default bg-muted/50')}
          />
        </div>

        <div>
          <label htmlFor="project-config-notes" className="block text-sm font-medium text-foreground">
            Notes
          </label>
          <textarea
            id="project-config-notes"
            rows={3}
            {...register('notes')}
            disabled={isView}
            className={cn(textareaClass, isView && 'cursor-default bg-muted/50')}
          />
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-border">
          {isView ? (
            <Button type="button" variant="outline" onClick={onClose}>
              Close
            </Button>
          ) : (
            <>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isCreate ? createMutation.isPending : updateMutation.isPending}
              >
                {isCreate
                  ? createMutation.isPending
                    ? 'Creating...'
                    : 'Create'
                  : updateMutation.isPending
                    ? 'Saving...'
                    : 'Save'}
              </Button>
            </>
          )}
        </div>
      </form>
    </CommonModal>
  );
}
